import {isFeatureEnabled} from 'feature-flags';
import {
  Card,
  DataStatus,
  Dropdown,
  DropdownDivider,
  DropdownItem,
  DropdownSearchItem,
  IconButton,
  Label,
  Separator,
  Switch,
} from 'platform/components';
import {
  Box,
  Grid,
  HStack,
  Icon,
  Integer,
  Show,
  Space,
  Text,
  ValueByDevice,
  VStack,
} from 'platform/foundation';

import {isTrue} from 'ramda-adjunct';

import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps} from 'shared/web';

import {BlacklistFlag} from '../../../../../../components/HitlistBlacklistFlag/components/BlacklistFlag';
import {HitlistFlag} from '../../../../../../components/HitlistBlacklistFlag/components/HitlistFlag';
import {SourcingType, useSourcingType} from '../../../../../../hooks/useSourcingType';
import {useMakeModelFilter} from '../../../../hooks/useMakeModelFilter';
import {FilterField} from '../../../FilterEditor/components/FilterField';
import {useFilters} from '../../../FiltersContext/FiltersContext';
import {FilterVehicleCategory} from '../FilterVehicleCategory/FilterVehicleCategory';
import {MakeCheckboxList} from './components/MakeCheckboxList';
import {MakesModelsSearch} from './components/MakesModelsSearch';
import {ModelFamiliesCheckboxTree} from './components/ModelFamiliesCheckboxTree';
import {TopMakes} from './components/TopMakes';
import {TopSellingCheckboxList} from './components/TopSellingCheckboxList';
import {useSuspiciousReasonSelection} from './hooks/useSuspiciousReasonSelection';

const FILTERS_GRID_MAX_HEIGHT_DEFAULT = 150;
/** The three panes stack below the notebook breakpoint, so full height means ~1800px of scroll. */
const FILTERS_GRID_MAX_HEIGHT_MOBILE = 40;
const FILTERS_GRID_MAX_HEIGHT: ValueByDevice<Integer> = [
  FILTERS_GRID_MAX_HEIGHT_MOBILE,
  FILTERS_GRID_MAX_HEIGHT_MOBILE,
  FILTERS_GRID_MAX_HEIGHT_DEFAULT,
];

export function FilterMakesModels(props: TestIdProps & {isCompact?: boolean}) {
  const {filters, onUpdateFilters} = useFilters();
  const {isLoading, isError} = useMakeModelFilter();
  const sourcingType = useSourcingType();
  const {
    isSuspiciousFilterActive,
    reasonSearch,
    setReasonSearch,
    reasonOptions,
    selectedReasons,
    toggleSuspiciousFilter,
    toggleReasons,
    selectAllReasons,
    unselectAllReasons,
  } = useSuspiciousReasonSelection();
  // The category is classifieds-only: `all_auctions` silently ignores `vehicle_type`
  const isVehicleCategoryVisible =
    isFeatureEnabled(featureFlags.SOURCING_VAN_FILTERS) &&
    sourcingType === SourcingType.CLASSIFIEDS;
  return (
    <>
      <FilterField name="vehicleCategory">
        <Show when={isVehicleCategoryVisible}>
          <FilterVehicleCategory data-testid={suffixTestId('vehicleCategory', props)} />
          <Separator />
        </Show>
      </FilterField>
      <Grid columns={props.isCompact ? 1 : [1, 1, 3]}>
        <Show whenFeatureEnabled={featureFlags.SOURCING_HITLIST_BLACKLIST}>
          <FilterField name="hitlistOnly">
            <Card colorStripe="green" variant="inlineGrey">
              <VStack spacing={2}>
                <HStack spacing={2} align="center">
                  <Switch
                    value={isTrue(filters.hitlistOnly)}
                    onChange={(checked) => onUpdateFilters('hitlistOnly', checked)}
                    label={i18n.t('entity.sourcing.filters.labels.hitlistOnly')}
                    data-testid={suffixTestId('hitlistOnly', props)}
                  />
                  <HitlistFlag />
                </HStack>
                <Box paddingLeft={12}>
                  <Text size="xSmall" color="tertiary">
                    {i18n.t('entity.sourcing.filters.labels.hitlistOnlyDescription')}
                  </Text>
                </Box>
              </VStack>
            </Card>
          </FilterField>
          <FilterField name="hideBlacklist">
            <Card colorStripe="red" variant="inlineGrey">
              <VStack spacing={2}>
                <HStack spacing={2} align="center">
                  <Switch
                    value={isTrue(filters.hideBlacklist)}
                    onChange={(checked) => onUpdateFilters('hideBlacklist', checked)}
                    label={i18n.t('entity.sourcing.filters.labels.hideBlacklist')}
                    data-testid={suffixTestId('hideBlacklist', props)}
                  />
                  <BlacklistFlag />
                </HStack>
                <Box paddingLeft={12}>
                  <Text size="xSmall" color="tertiary">
                    {i18n.t('entity.sourcing.filters.labels.hideBlacklistDescription')}
                  </Text>
                </Box>
              </VStack>
            </Card>
          </FilterField>
        </Show>
        <Show whenFeatureEnabled={featureFlags.SOURCING_SUSPICIOUS_VEHICLES}>
          <FilterField name="hideSuspicious">
            <Card colorStripe="orange" variant="inlineGrey">
              <VStack spacing={2}>
                <HStack spacing={2} align="center">
                  <Switch
                    value={isSuspiciousFilterActive}
                    onChange={toggleSuspiciousFilter}
                    label={i18n.t('entity.sourcing.filters.labels.hideSuspicious')}
                    data-testid={suffixTestId('hideSuspicious', props)}
                  />
                  <Icon value="alert/warning" color="severity.warning" size={5} />
                  <Show whenFeatureEnabled={featureFlags.SOURCING_SUSPICIOUS_REASON_FILTER}>
                    <Dropdown
                      hasSearch
                      isHeightLimited
                      data-testid={suffixTestId('excludeSuspiciousReasons', props)}
                      dropdownControl={
                        <IconButton
                          icon="navigation/expand_more"
                          size="small"
                          priority="default"
                          aria-label={i18n.t('general.labels.reason')}
                          data-testid={suffixTestId('excludeSuspiciousReasons-trigger', props)}
                        />
                      }
                    >
                      <>
                        <DropdownSearchItem value={reasonSearch} onChange={setReasonSearch} />
                        <DropdownItem
                          label={i18n.t('general.actions.selectAll')}
                          onClick={selectAllReasons}
                        />
                        <DropdownItem
                          label={i18n.t('general.actions.unselectAll')}
                          onClick={unselectAllReasons}
                        />
                        <DropdownDivider />
                        {reasonOptions.map((option) => {
                          const isSelected = option.values.every((reason) =>
                            selectedReasons.includes(reason)
                          );
                          return (
                            <DropdownItem
                              key={option.label}
                              label={option.label}
                              prefix={
                                <Box width={4} height={4}>
                                  <Show when={isSelected}>
                                    <Icon value="alert/warning" color="severity.warning" size={4} />
                                  </Show>
                                </Box>
                              }
                              closeOnClick={false}
                              onClick={() => toggleReasons(option.values)}
                            />
                          );
                        })}
                      </>
                    </Dropdown>
                  </Show>
                </HStack>
                <Box paddingLeft={12}>
                  <Text size="xSmall" color="tertiary">
                    {i18n.t('entity.sourcing.filters.labels.hideSuspiciousDescription')}
                  </Text>
                </Box>
              </VStack>
            </Card>
          </FilterField>
        </Show>
      </Grid>
      <Show
        when={
          !props.isCompact &&
          (isFeatureEnabled(featureFlags.SOURCING_HITLIST_BLACKLIST) ||
            isFeatureEnabled(featureFlags.SOURCING_SUSPICIOUS_VEHICLES))
        }
      >
        <Separator />
      </Show>
      <FilterField name="make">
        <DataStatus isLoading={isLoading} isError={isError}>
          <VStack spacing={4}>
            <MakesModelsSearch data-testid={suffixTestId('makesModelsSearch', props)} />
            <Grid columns={[1, 1, 3]}>
              <Box paddingHorizontal={4} maxHeight={FILTERS_GRID_MAX_HEIGHT} overflow="auto">
                <VStack spacing={2}>
                  <Label>{i18n.t('page.filters.labels.topMakes')}</Label>
                  <TopMakes data-testid={suffixTestId('topMakes', props)} />
                  <Label>{i18n.t('page.filters.labels.makes')}</Label>
                  <MakeCheckboxList data-testid={suffixTestId('makes', props)} />
                </VStack>
              </Box>
              <Box paddingHorizontal={4} maxHeight={FILTERS_GRID_MAX_HEIGHT} overflow="auto">
                <VStack spacing={2}>
                  <Label>{i18n.t('page.filters.labels.models')}</Label>
                  <ModelFamiliesCheckboxTree data-testid={suffixTestId('modelFamilies', props)} />
                </VStack>
              </Box>
              <Box paddingHorizontal={4} maxHeight={FILTERS_GRID_MAX_HEIGHT} overflow="auto">
                <Label tooltip={i18n.t('page.filters.labels.topSellingTooltip')}>
                  {i18n.t('page.filters.labels.topSelling')}
                </Label>
                <Space vertical={4} />
                <TopSellingCheckboxList data-testid={suffixTestId('topSelling', props)} />
              </Box>
            </Grid>
          </VStack>
        </DataStatus>
      </FilterField>
    </>
  );
}
